/**
 * Sticky banner shown across every signed-in page: surfaces the user's
 * top current leak as a single line + a "Drill it" CTA. The point is
 * that the $50/mo differentiator (leak detection) should be visible on
 * every screen, not buried behind the Skills tab.
 *
 * Rendering rules:
 *   - Hidden while the leak report query is loading (avoid layout jank)
 *   - Hidden when the report is in the insufficient-signals state
 *   - Hidden when patterns is empty (rare — model couldn't find anything)
 *   - Otherwise: show the highest-severity pattern's title + CTA
 *   - Sits under the page header/main content area, sticky to the top
 */

import { useQuery } from "@tanstack/react-query";

import { fetchLeakReport } from "@/shared/api";
import { drillLeakRoute } from "@/shared/chatRoute";
import type { LeakPattern } from "@/shared/models";
import { DrillLaunchLink } from "@/shared/components/DrillLaunchLink";
import { ChevronRight, ICON_STROKE, Target } from "@/shared/components/icons";

const SEVERITY_RANK: Record<LeakPattern["severity"], number> = {
  high: 0,
  medium: 1,
  low: 2,
};

export function TopLeakBanner() {
  const { data } = useQuery({
    queryKey: ["leak-report"],
    queryFn: fetchLeakReport,
    // Refetch on focus / mount — earlier 60s staleTime caused the banner
    // (and the underlying TanStack cache the Skills page reads from)
    // to look stale after running drills on another platform. Backend
    // already caches leak reports per-user, so refetching is cheap.
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  });

  if (!data || data.insufficientSignals || data.patterns.length === 0) {
    return null;
  }

  const top = [...data.patterns].sort(
    (a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity],
  )[0]!;

  const severityRing = {
    high: "ring-1 ring-red-500/25 bg-red-500/[0.06]",
    medium: "ring-1 ring-orange-400/25 bg-orange-400/[0.06]",
    low: "ring-1 ring-yellow-400/25 bg-yellow-400/[0.06]",
  }[top.severity];

  const severityDot = {
    high: "bg-red-500",
    medium: "bg-orange-400",
    low: "bg-yellow-400",
  }[top.severity];

  return (
    <div
      className={[
        "sticky top-0 z-20 border-b border-border/60 backdrop-blur supports-[backdrop-filter]:bg-bg/75",
        severityRing,
      ].join(" ")}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-2.5 flex items-center gap-3">
        <span className="inline-flex items-center gap-2 flex-shrink-0 text-[10px] font-semibold uppercase tracking-[0.15em] text-text-muted/90">
          <span className={`w-1.5 h-1.5 rounded-full ${severityDot}`} />
          Top leak
        </span>
        <span className="text-sm text-text truncate flex-1 min-w-0">
          {top.title}
        </span>
        <DrillLaunchLink
          to={drillLeakRoute(top)}
          className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-accent hover:text-accent/80 transition"
        >
          <Target size={12} strokeWidth={ICON_STROKE} />
          Drill it
          <ChevronRight size={12} strokeWidth={ICON_STROKE} />
        </DrillLaunchLink>
      </div>
    </div>
  );
}
